import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { IMAGES } from '../../assets/images/placeholder';
import { FaUser, FaSignInAlt, FaTachometerAlt, FaSignOutAlt, FaCog, FaStar, FaQuestionCircle } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 2rem;
  z-index: 1000;
  transition: all 0.3s ease;
  background-color: var(--bg-primary);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
`;

const Logo = styled(Link)`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  
  img {
    height: 40px;
    width: auto;
    object-fit: contain;
  }
`;

const TextColored = styled.span`
  color: var(--primary-color);
`

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: var(--font-size-md);
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

interface NavItemsProps {
  isOpen: boolean;
}

const NavItems = styled.div<NavItemsProps>`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  .mobile-only {
    display: none;
  }
  
  @media (max-width: 768px) {
    position: fixed;
    top: 80px;
    left: 0;
    width: 100%;
    height: calc(100vh - 80px);
    background-color: var(--bg-primary);
    flex-direction: column;
    padding: 1rem 0;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
    z-index: 100;
    gap: 0;
    overflow-y: auto;
    
    .mobile-only {
      display: block;
      width: 100%;
    }
    
    a {
      width: 100%;
      padding: 1rem;
      text-align: left;
      display: block;
    }
  }
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled(Link)`
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: var(--border-radius-sm);
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--primary-color);
    background-color: var(--bg-secondary);
  }
  
  &.active {
    color: var(--primary-color);
    font-weight: 600;
  }
`;

const ServicesDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: var(--bg-primary);
  min-width: 250px;
  box-shadow: var(--shadow-md);
  border-radius: var(--radius-sm);
  overflow: hidden;
  z-index: var(--z-dropdown);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all var(--transition-normal);
  
  ${NavItem}:hover & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--text-primary);
  cursor: pointer;
  padding: var(--spacing-sm);
  
  @media (max-width: 768px) {
    display: block;
  }

  &:hover {
    color: var(--primary-color);
    transform: none;
    box-shadow: none;
  }
`;

const Hamburger = styled.div`
  width: 20px;
  height: 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;

  div {
    width: 100%;
    height: 2px;
    background-color: var(--text-primary);
    transition: all 0.3s ease;
  }

  &.active div:nth-child(1) {
    transform: translateY(8px) rotate(45deg);
  }

  &.active div:nth-child(2) {
    opacity: 0;
  }

  &.active div:nth-child(3) {
    transform: translateY(-5px) rotate(-45deg);
  }
`;

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  padding: 0 var(--spacing-sm);
  
  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const ScrollToTopNavLink = ({ to, children, onClick }: { to: string; children: React.ReactNode, onClick?: () => void }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    if (onClick) {
      onClick();
    }
    
    navigate(to);
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <NavLink to={to} onClick={handleClick}>
      {children}
    </NavLink>
  );
};

const SubNavLink = ({ to, children, onClick }: { to: string; children: React.ReactNode, onClick?: () => void }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    if (onClick) {
      onClick();
    }
    
    navigate(to);
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <Link 
      to={to} 
      onClick={handleClick}
      style={{ 
        color: 'var(--text-secondary)',
        padding: '12px 20px',
        textDecoration: 'none',
        display: 'block',
        transition: 'background-color 0.3s ease'
      }}
    >
      {children}
    </Link>
  );
};

const LoginButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: var(--button-text);
  font-weight: 600;
  border-radius: var(--border-radius-md);
  transition: all 0.3s ease;
  text-decoration: none;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
    background-color: var(--primary-color-dark);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    margin-top: 1rem;
    width: auto;
    justify-content: center;
  }
`;

const UserMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  min-width: 200px;
  z-index: 9999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease-in-out;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
    opacity: ${props => props.isOpen ? 1 : 0};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-100%)'};
    z-index: 9999;
  }
`;

const UserMenuItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: var(--text-primary);
  text-decoration: none;
  transition: background-color 0.2s;
  width: 100%;
  
  &:hover {
    background-color: var(--bg-secondary);
  }
  
  svg {
    margin-right: 0.75rem;
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
  }
`;

const UserMenuDivider = styled.div`
  height: 1px;
  background-color: var(--border-color);
  margin: 0.5rem 0;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  border-radius: var(--border-radius-md);
  
  &:hover {
    background-color: var(--bg-secondary);
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  overflow: hidden;
  
  svg {
    font-size: 1.25rem;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileLanguageContainer = styled.div`
  margin-top: 0.5rem;
  width: 100%;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userSession, setUserSession] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  useEffect(() => {
    // Vérifier si l'utilisateur est connecté
    const sessionData = localStorage.getItem('userSession');
    console.log('Session data from localStorage:', sessionData);
    
    if (sessionData) {
      try {
        const parsedData = JSON.parse(sessionData);
        console.log('Parsed session data:', parsedData);
        setUserSession(parsedData);
      } catch (error) {
        console.error('Error parsing user session:', error);
        localStorage.removeItem('userSession');
        setUserSession(null);
      }
    } else {
      console.log('No session data found in localStorage');
    }
  }, [location]);

  useEffect(() => {
    console.log('Current userSession:', userSession);
  }, [userSession]);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('userSession');
    setUserSession(null);
    navigate('/');
    setIsUserMenuOpen(false);
  };

  return (
    <NavbarContainer>
      <NavbarContent>
        <Logo to="/" onClick={() => {
          closeMenu();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}>
          Taxi<TextColored>Moove</TextColored>
        </Logo>

        <NavItems isOpen={isMenuOpen} className={isMenuOpen ? 'active' : ''}>
          <NavItem>
            <ScrollToTopNavLink to="/services" onClick={closeMenu}>
              {t('nav.services')}
            </ScrollToTopNavLink>
          </NavItem>
          <NavItem>
            <ScrollToTopNavLink to="/about" onClick={closeMenu}>
              {t('nav.about')}
            </ScrollToTopNavLink>
          </NavItem>
          <NavItem>
            <ScrollToTopNavLink to="/contact" onClick={closeMenu}>
              {t('nav.contact')}
            </ScrollToTopNavLink>
          </NavItem>
          
          {/*{userSession ? (
            <>
              <NavItem className="mobile-only">
                <UserMenuItem to="/profile" onClick={closeMenu}>
                  <FaUser />
                  <span>{t('common.profile')}</span>
                </UserMenuItem>
              </NavItem>
              <NavItem className="mobile-only">
                <UserMenuItem to="/profile/settings" onClick={closeMenu}>
                  <FaCog />
                  <span>{t('common.settings')}</span>
                </UserMenuItem>
              </NavItem>
              <NavItem className="mobile-only">
                <UserMenuItem to="/profile/favorites" onClick={closeMenu}>
                  <FaStar />
                  <span>{t('common.favorites')}</span>
                </UserMenuItem>
              </NavItem>
              <NavItem className="mobile-only">
                <UserMenuItem to="/profile/help" onClick={closeMenu}>
                  <FaQuestionCircle />
                  <span>{t('common.help')}</span>
                </UserMenuItem>
              </NavItem>
              <NavItem className="mobile-only">
                <UserMenuItem to="/profile/logout" onClick={() => {
                  handleLogout();
                  closeMenu();
                }}>
                  <FaSignOutAlt />
                  <span>{t('common.logout')}</span>
                </UserMenuItem>
              </NavItem>
            </>
          ) : (
            <NavItem className="mobile-only" style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}>
              <LoginButton to="/connexion" onClick={closeMenu}>
                <FaSignInAlt />
                <span>{t('common.login')}</span>
              </LoginButton>
            </NavItem>
          )}*/}
          
          <MobileLanguageContainer>
            <LanguageSelector isMobile={true} />
          </MobileLanguageContainer>
        </NavItems>
        
        <TopRight>
          <LanguageSelector />
          
          {/*{!userSession ? (
            <LoginButton to="/connexion">
              <FaSignInAlt />
              <span>{t('common.login')}</span>
            </LoginButton>
          ) : (
            <>
              <UserButton onClick={toggleUserMenu}>
                <UserAvatar>
                  <FaUser />
                </UserAvatar>
              </UserButton>
              <UserMenu isOpen={isUserMenuOpen}>
                <UserMenuItem to="/profile" onClick={() => setIsUserMenuOpen(false)}>
                  <FaUser />
                  <span>{t('common.profile')}</span>
                </UserMenuItem>
                <UserMenuDivider />
                <UserMenuItem to="/profile/settings" onClick={() => setIsUserMenuOpen(false)}>
                  <FaCog />
                  <span>{t('common.settings')}</span>
                </UserMenuItem>
                <UserMenuItem to="/profile/favorites" onClick={() => setIsUserMenuOpen(false)}>
                  <FaStar />
                  <span>{t('common.favorites')}</span>
                </UserMenuItem>
                <UserMenuItem to="/profile/help" onClick={() => setIsUserMenuOpen(false)}>
                  <FaQuestionCircle />
                  <span>{t('common.help')}</span>
                </UserMenuItem>
                <UserMenuItem to="/profile/logout" onClick={() => {
                  handleLogout();
                  setIsUserMenuOpen(false);
                }}>
                  <FaSignOutAlt />
                  <span>{t('common.logout')}</span>
                </UserMenuItem>
              </UserMenu>
            </>
          )}*/}
        </TopRight>

        <MobileMenuButton onClick={toggleMenu}>
          <Hamburger className={isMenuOpen ? 'active' : ''}>
            <div></div>
            <div></div>
            <div></div>
          </Hamburger>
        </MobileMenuButton>
      </NavbarContent>
    </NavbarContainer>
  );
};

export default Navbar; 